import classNames from 'classnames';
import Checkbox from 'components/atoms/Checkbox';
import Icon from 'components/atoms/Icon';
import { join, map } from 'lodash';
import { useEffect, useState } from 'react';
import { SelectOption } from 'utils/frontendTypes';

interface CheckboxSelectProps {
  options: SelectOption[];
  open?: boolean;
  handleOpen?: () => void;
  onBlur?: () => void;
  selectedOptions: SelectOption[];
  label: string;
  placeholder?: string;
  onChange: (option: SelectOption, checked: boolean) => void;
  className?: string;
  dataDurableId: string;
  showAllSelectedLabels?: boolean;
  radius?: string;
  inline?: boolean;
  labelClassName?: string;
  menuClassName?: string;
}

const CheckboxSelect = ({
  options,
  open,
  handleOpen,
  onBlur,
  selectedOptions,
  label,
  onChange,
  placeholder,
  className,
  dataDurableId,
  showAllSelectedLabels,
  radius = 'rounded-sm',
  inline,
  labelClassName,
  menuClassName,
}: CheckboxSelectProps) => {
  const [isOpen, setIsOpen] = useState(open);

  const displayLabel =
    selectedOptions?.length > 0
      ? showAllSelectedLabels
        ? join(map(selectedOptions, 'label'), ', ')
        : selectedOptions[0].label
      : placeholder;

  const isChecked = (option: SelectOption) => map(selectedOptions, 'value').includes(option.value);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  return (
    <button
      type="button"
      onClick={() => {
        setIsOpen(!isOpen);
        return handleOpen && handleOpen();
      }}
      onBlur={() => {
        if (!inline) {
          setIsOpen(false);
        }

        return onBlur && onBlur();
      }}
      data-durable-id={dataDurableId}
      data-intercom-target={dataDurableId}
      className={classNames(
        'relative flex justify-between text-left bg-white text-glooCharcoal leading-tight text-xs md:text-sm py-1 md:py-2 px-4 border border-charcoal-200 cursor-pointer',
        inline ? 'flex-col' : 'items-center',
        radius,
        {
          'border-glooNavy-950': isOpen && !inline,
        },
        className,
      )}
    >
      <div className="w-full flex items-center justify-between">
        <div className="flex flex-1 max-w-[80%]">
          <div className="mr-2 shrink-0">{label}</div>
          <div className={classNames('font-bold', 'truncate', labelClassName)}>{displayLabel}</div>
        </div>
        <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={24} className="text-glooMedGray" />
      </div>

      <div
        className={classNames(
          'py-2',
          !inline &&
            'flex flex-col px-4 bg-white rounded-sm border border-charcoal-200 absolute z-10 max-h-72 overflow-auto inbox-scrollbar-y left-0 min-w-52 w-full top-[100%] mt-[2px] shadow-xl',
          inline && 'w-full',
          {
            hidden: !isOpen,
          },
          menuClassName,
        )}
      >
        {options?.map((option) => (
          <Checkbox
            key={option.value}
            checked={isChecked(option)}
            wrapClassName="hover:bg-glooNavy-100 p-2 cursor-pointer"
            onChange={() => onChange(option, isChecked(option))}
            label={option.label}
            labelStyle={{ wordBreak: 'break-word' }}
          />
        ))}
      </div>
    </button>
  );
};

export default CheckboxSelect;
